import {
  CheckoutHanger,
  CreateBookingRequest,
  CreateGreeterEventRequest,
  CreateProductRequest,
  DeleteActivePeriods,
  DeleteCategory,
  DeleteTicketEvent,
  FetchBookingsRequest,
  FetchCategory,
  FetchGreeterEventsRequest,
  FetchHangerHistoryItems,
  FetchHangers,
  FetchIntegrationBySlug,
  FetchOnlinePOSProductConnection,
  FetchOrdersRequest,
  FetchStripeLoginLinkResult,
  FetchStripeOnboardingLinkRequest,
  FetchTicket,
  FetchTicketEvent,
  FetchTicketEvents,
  FetchTicketOrders,
  FetchTickets,
  FetchVenueFeatureFlag,
  IVenueApi,
  LockTableArgs,
  RejectBookingRequest,
  RequestOptions,
  SaveActivePeriods,
  SaveCategory,
  SaveOnlinePOSProductConnection,
  SaveProduct,
  SaveTicket,
  SaveTicketEvent,
  SaveTickets,
  SearchHangers,
  UnlockTableArgs,
  UpdateBookingRequest,
  UpdateGreeterEventRequest,
  UpdateProductRequest,
  UpdateSpecialOpeningHoursArgs,
  UpdateTableServiceSettingsArgs,
  UpdateVenueRequest,
  UploadAssetRequest,
} from "./IVenueApi";
import { UrlBuilder } from "@greeter/url";
import {
  Area,
  Booking,
  Customer,
  GreeterEvent,
  LockedTable,
  Menu,
  MusicGenre,
  Order,
  PricedCapacity,
  Product,
  TableId,
  TableServiceOrder,
  TableServiceSettings,
  Theme,
  Venue,
  ImageAsset,
  SpecialOpeningHours,
  Bundle,
  BundleSchema,
  CustomerSchema,
  ActivePeriod,
  CategoryCategory,
  Category,
  SpecialPricePeriod,
  SpecialPricePeriodSchema,
  CategorySchema,
  SpecialPricePeriodsSchema,
  ActivePeriodsSchema,
  CategoriesSchema,
  VenueProduct,
  VenueProductsSchema,
  VenueProductSchema,
  VenueSchema,
  BookingSchema,
  OrderSchema,
  GreeterEventSchema,
  IntegrationSchema,
  OnlinePOSProductConnectionSchema,
  VenueFeatureFlagSchema,
  VenueTicketEventSchema,
  VenueTicketSchema,
  TicketOrderSchema,
  LockedTableSchema,
  TableIdSchema,
  HangerSchema,
  HangerHistoryItemSchema,
} from "@greeter/core";
import { uniq } from "lodash";
import {
  BaseProxy,
  AreaDeserializer,
  FetchTicketOrder,
  GreeterEventDeserializer,
  IDeserializer,
  PageRequest,
  ThemeDeserializer,
  ManySpecialOpeningHoursDeserializer,
  ParseError,
  ApiVersions,
  withApiVersion,
  EmptyBody,
} from "@greeter/api";
import {
  DatePeriod,
  TimeOfDay,
  toISODateString,
  toISOString,
} from "@greeter/date";
import { Config } from "@greeter/config";
import { IAuthentication } from "@greeter/auth";
import { parseISO } from "date-fns";
import { jsonify } from "@greeter/json";
import * as z from "zod";

function parseDate(dateIsoString: string) {
  return parseISO(dateIsoString);
}

function deserializeTableServiceOrder(json: any) {
  const result = { ...json };

  result.createdAt = parseDate(json.createdAt);

  if (result.acceptedAt) {
    result.acceptedAt = parseDate(json.acceptedAt);
  }

  if (result.completedAt) {
    result.completedAt = parseDate(json.completedAt);
  }

  if (result.rejectedAt) {
    result.rejectedAt = parseDate(json.rejectedAt);
  }

  return result as TableServiceOrder;
}

function deserializeMenu(json: any) {
  json.products.forEach((p: any) => {
    p.priceChanges.forEach((pc: any) => {
      pc.period.from = TimeOfDay.parse(pc.period.from);
      pc.period.to = TimeOfDay.parse(pc.period.to);
    });
  });

  return json as Menu;
}

export class VenueApi extends BaseProxy implements IVenueApi {
  url: URL;

  constructor(
    config: Config,
    authentication: IAuthentication,
    private areaDeserializer: IDeserializer<Area> = new AreaDeserializer(),
    private greeterEventDeserializer: IDeserializer<GreeterEvent> = new GreeterEventDeserializer(),
    private themeDeserializer: IDeserializer<Theme> = new ThemeDeserializer(),
  ) {
    super(config, authentication);
    this.url = new URL(`${this.getBaseUrl()}/venue-api`);
    this.apiVersion = ApiVersions.V2;
  }

  fetchHangers: FetchHangers = async () => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("wardrobe")
      .addPath("hangers")
      .build();
    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    if (!Array.isArray(json)) return [HangerSchema.parse(json)];
    return json.map((j) => HangerSchema.parse(j));
  };

  fetchHangerHistoryItems: FetchHangerHistoryItems = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("wardrobe")
      .addPath("hangers")
      .addPath(request.hangerId)
      .addPath("history")
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    if (!Array.isArray(json)) return [HangerHistoryItemSchema.parse(json)];
    return json.map((j) => HangerHistoryItemSchema.parse(j));
  };

  checkoutHanger: CheckoutHanger = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("wardrobe")
      .addPath("hangers")
      .addPath(request.hangerId)
      .addPath("checkout")
      .build();
    await this.post(url, EmptyBody, { authorize: true });
  };

  searchHangers: SearchHangers = async (request, options) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("wardrobe")
      .addPath("hangers")
      .addPath("search")
      .addQuery("name", encodeURIComponent(request.name))
      .build();

    const response = await this.get(url, {
      authorize: true,
      abortController: options?.abortController,
    });
    const json = await response.json();
    if (!Array.isArray(json)) return [HangerSchema.parse(json)];
    return json.map((j) => HangerSchema.parse(j));
  };

  fetchTicketOrder: FetchTicketOrder = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketOrders")
      .addPath(request.id)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    return TicketOrderSchema.parse(json);
  };

  fetchTicketOrders: FetchTicketOrders = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketOrders")
      .addQueries(request)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();

    return json.map(TicketOrderSchema.parse);
  };

  fetchTicket: FetchTicket = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("tickets")
      .addPath(request.id)
      .build();

    const response = await this.get(url, {
      authorize: true,
    });

    const json = await response.json();
    return VenueTicketSchema.parse(json);
  };

  fetchTickets: FetchTickets = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("tickets")
      .addQueries(request)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return json.map(VenueTicketSchema.parse);
  };

  saveTickets: SaveTickets = async (request) => {
    const url = new UrlBuilder(this.getUrl()).addPath("tickets").build();
    const response = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();
    return json.map(VenueTicketSchema.parse);
  };

  saveTicket: SaveTicket = async (request) => {
    const url = new UrlBuilder(this.getUrl()).addPath("tickets").build();

    const response = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();

    return VenueTicketSchema.parse(json);
  };

  fetchTicketEvents: FetchTicketEvents = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketEvents")
      .addQuery("from", request.period.from.toISOString())
      .addQuery("to", request.period.to.toISOString())
      .build();

    const result = await this.get(url, { authorize: true });
    const json = await result.json();
    return json.map(VenueTicketEventSchema.parse);
  };

  fetchTicketEvent: FetchTicketEvent = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketEvents")
      .addPath(request.id)
      .build();

    const result = await this.get(url, { authorize: true });
    const json = await result.json();

    return VenueTicketEventSchema.parse(json);
  };

  saveTicketEvent: SaveTicketEvent = async (request) => {
    const url = new UrlBuilder(this.getUrl()).addPath("ticketEvents").build();

    const result = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await result.json();

    return VenueTicketEventSchema.parse(json);
  };

  deleteTicketEvent: DeleteTicketEvent = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("ticketEvents")
      .addPath(request.id)
      .build();

    await this.delete(url, EmptyBody, { authorize: true });
  };

  fetchVenueFeatureFlag: FetchVenueFeatureFlag = async (request) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("featureFlags")
      .addPath(request.name)
      .build();

    const result = await this.get(url, { authorize: true });
    const json = await result.json();

    return VenueFeatureFlagSchema.parse(json);
  };

  fetchOnlinePOSProductConnection: FetchOnlinePOSProductConnection = async (
    productId,
  ) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("onlinepos")
      .addPath("productConnections")
      .addQuery("productId", productId)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    const parsed = OnlinePOSProductConnectionSchema.parse(json);
    return parsed;
  };

  saveOnlinePOSProductConnection: SaveOnlinePOSProductConnection = async (
    connection,
  ) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("onlinepos")
      .addPath("productConnections")
      .build();

    const response = await this.post(url, JSON.stringify(connection), {
      authorize: true,
    });
    const json = await response.json();
    const parsed = OnlinePOSProductConnectionSchema.parse(json);
    return parsed;
  };

  fetchIntegrationBySlug: FetchIntegrationBySlug = async (slug) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("integrations")
      .addQuery("slug", slug)
      .build();

    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    const parsed = IntegrationSchema.parse(json);
    return parsed;
  };

  /**
   * @throws {ParseError}
   * when fails to parse the json
   */
  saveActivePeriods: SaveActivePeriods = async (activePeriods) => {
    const url = new UrlBuilder(this.getUrl()).addPath("activePeriods").build();
    const result = await this.post(url, JSON.stringify(activePeriods), {
      authorize: true,
    });
    try {
      return ActivePeriodsSchema.parse(await result.json());
    } catch (error) {
      throw new ParseError(
        "failed to parse ActivePeriod",
        error instanceof Error ? error : undefined,
      );
    }
  };

  deleteActivePeriods: DeleteActivePeriods = async (ids) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("activePeriods")
      .addQuery("ids", ids)
      .build();
    await this.delete(url, EmptyBody, { authorize: true });
  };

  saveProduct: SaveProduct = async (p) => {
    const url = new UrlBuilder(this.getUrl()).addPath("products").build();
    await this.post(url, JSON.stringify(p), { authorize: true });
  };

  deleteCategory: DeleteCategory = async (id: string) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("categories")
      .addPath(id)
      .build();

    await this.delete(url, EmptyBody, { authorize: true });
  };

  fetchCategory: FetchCategory = async (id: string) => {
    const url = new UrlBuilder(this.getUrl())
      .addPath("categories")
      .addPath(id)
      .build();
    const result = await this.get(url, { authorize: true });
    const parsed = CategorySchema.safeParse(await result.json());

    if (parsed.error) {
      console.error(result, parsed.error);
      throw parsed.error;
    }

    return parsed.data;
  };

  saveCategory: SaveCategory = async (c) => {
    const url = new UrlBuilder(this.getUrl()).addPath("categories").build();
    await this.post(url, JSON.stringify(c), { authorize: true });
  };

  /**
   * @throws {ParseError} if the returning object is not of type array or if the json fails to parse with {@see SpecialPricePeriodSchema}
   */
  async fetchSpecialPricePeriodsByProducts(productIds: Array<string>) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("specialPricePeriods")
      .addQuery("productIds", productIds)
      .build();

    const json = await (await this.get(url, { authorize: true })).json();

    if (!Array.isArray(json)) {
      throw new ParseError(
        `Failed to fetch special price periods, expected it to be an array got ${typeof json}`,
      );
    }

    try {
      const result = json.map((j) => SpecialPricePeriodSchema.parse(j));
      return result;
    } catch (error) {
      throw new ParseError(
        "failed to parse the json to SpecialPricePeriodSchema",
        error instanceof Error ? error : undefined,
      );
    }
  }

  /**
   * @throws {ParseError} if not an array or cannot be parsed by @see {@type SpecialPricePeriod}
   */
  async fetchSpecialPricePeriods(
    productId: string,
  ): Promise<Array<SpecialPricePeriod>> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addPath(productId)
      .addPath("specialPricePeriods")
      .build();

    const schema = z.array(SpecialPricePeriodSchema);
    const result = await this.get(url, { authorize: true });

    try {
      const parsed = schema.parse(await result.json());
      return parsed;
    } catch (error) {
      throw new ParseError(
        "failed to parse using SpecialPricePeriodSchema",
        error instanceof Error ? error : undefined,
      );
    }
  }

  /**
   * @throws {ParseError} if not an array or cannot be parsed by {@link SpecialPricePeriod}
   */
  async saveSpecialPricePeriods(
    specialPricePeriods: Array<SpecialPricePeriod>,
  ): Promise<Array<SpecialPricePeriod>> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("specialPricePeriods")
      .build();

    const r = await (
      await this.post(url, JSON.stringify(specialPricePeriods), {
        authorize: true,
      })
    ).json();

    try {
      return SpecialPricePeriodsSchema.parse(r);
    } catch (error) {
      throw new ParseError(
        "could not parse special periods",
        error instanceof Error ? error : undefined,
      );
    }
  }

  async deleteSpecialPricePeriods(ids: Array<string>) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("specialPricePeriods")
      .addQuery("ids", ids)
      .build();

    await this.delete(url, EmptyBody, { authorize: true });
  }

  async fetchActivePeriods(
    bundleId: string | Array<string>,
  ): Promise<Array<ActivePeriod>> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("activePeriods")
      .addQuery("productIds", bundleId)
      .build();

    const result = await this.get(url, { authorize: true });

    try {
      return ActivePeriodsSchema.parse(await result.json());
    } catch (error) {
      throw new ParseError(
        "failed to parse ActivePeriod with error",
        error instanceof Error ? error : undefined,
      );
    }
  }

  async fetchCategoryCategories(): Promise<Array<CategoryCategory>> {
    console.warn(`${this.fetchCategoryCategories.name} is not implemented`);
    return [];
  }

  async fetchCategories(): Promise<Array<Category>> {
    const url = new UrlBuilder(this.getUrl()).addPath("categories").build();
    const result = await this.get(url, { authorize: true });
    return CategoriesSchema.parse(await result.json());
  }

  async fetchProductsByCategory(
    categoryId: string,
  ): Promise<Array<VenueProduct>> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addQuery("categoryId", categoryId)
      .build();

    const result = await (await this.get(url, { authorize: true })).json();

    return VenueProductsSchema.parse(result);
  }

  async saveBundle(request: Bundle): Promise<Bundle> {
    const url = new UrlBuilder(this.getUrl()).addPath("bundles").build();
    const response = await (
      await this.post(url, jsonify(request), {
        authorize: true,
      })
    ).json();
    return BundleSchema.parse(response);
  }

  async fetchBundle(id: string): Promise<Bundle> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bundles")
      .addPath(id)
      .build();

    const response = await (await this.get(url, { authorize: true })).json();
    try {
      const parsed = BundleSchema.parse(response);
      return parsed;
    } catch (error) {
      console.error(
        `[${this.fetchBundle.name}] failed to fetch bundle with error`,
        error,
      );

      throw error;
    }
  }

  async fetchBundles(): Promise<Array<Bundle>> {
    const url = new UrlBuilder(this.getUrl()).addPath("bundles").build();

    const response = await (await this.get(url, { authorize: true })).json();

    return response.map((obj: any) => BundleSchema.parse(obj));
  }

  async deleteBundle(id: string): Promise<void> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bundles")
      .addPath(id)
      .build();

    await this.delete(url, EmptyBody, { authorize: true });
  }

  async updateMenu(menu: Menu): Promise<Menu> {
    const url = new UrlBuilder(this.getUrl()).addPath("menus").build();
    const body = JSON.stringify(menu);
    const response = await (
      await this.post(url, body, { authorize: true })
    ).json();

    return deserializeMenu(response);
  }

  async fetchMenus(): Promise<Menu[]> {
    const url = new UrlBuilder(this.getUrl()).addPath("menus").build();

    const response = await (await this.get(url, { authorize: true })).json();

    return response.map(deserializeMenu);
  }

  async completeTableServiceOrder(id: string) {
    const url = this.createTableServiceOrderBuilder()
      .addPath(id)
      .addPath("complete")
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();

    return deserializeTableServiceOrder(response);
  }

  async rejectTableServiceOrder(id: string) {
    const url = this.createTableServiceOrderBuilder()
      .addPath(id)
      .addPath("reject")
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();

    return deserializeTableServiceOrder(response);
  }

  async acceptTableServiceOrder(id: string) {
    const url = this.createTableServiceOrderBuilder()
      .addPath(id)
      .addPath("accept")
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();

    return deserializeTableServiceOrder(response);
  }

  async fetchTableServiceOrders(period: DatePeriod) {
    const url = this.createTableServiceOrderBuilder()
      .addQuery("from", toISOString(period.from))
      .addQuery("to", toISOString(period.to))
      .build();

    const response = await (await this.get(url, { authorize: true })).json();

    return response.map(deserializeTableServiceOrder);
  }

  createTableServiceOrderBuilder(): UrlBuilder {
    return new UrlBuilder(this.getUrl()).addPath("tableServiceOrders");
  }

  createTableServiceSettingsBuilder(): UrlBuilder {
    return new UrlBuilder(this.getUrl()).addPath("tableServiceSettings");
  }

  async fetchTableServiceSettings() {
    const url = this.createTableServiceSettingsBuilder().build();

    const response = await (await this.get(url, { authorize: true })).json();

    return response as TableServiceSettings;
  }

  async updateTableServiceSettings(args: UpdateTableServiceSettingsArgs) {
    const url = this.createTableServiceSettingsBuilder().build();

    const response = await this.patch(url, JSON.stringify(args), {
      authorize: true,
    });
    const json = await response.json();

    return json as TableServiceSettings;
  }

  async createBooking(request: CreateBookingRequest): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl()).addPath("bookings").build();
    const response = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();
    return BookingSchema.parse(json);
  }

  async updateBooking(request: UpdateBookingRequest): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl()).addPath("bookings").build();
    const response = await this.patch(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();
    return BookingSchema.parse(json);
  }

  async fetchFloorPlan(): Promise<Area[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("floorPlan")
      .build();

    const response = await this.get(url, { authorize: true });
    const deserialized = this.areaDeserializer.deserializeAll(
      await response.json(),
    );

    return deserialized;
  }

  async fetchCustomers(ids: string[]): Promise<Customer[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("customers")
      .addQuery("ids", uniq(ids).join(","))
      .build();

    const response = (
      await (await this.get(url, { authorize: true })).json()
    ).map(CustomerSchema.parse);

    return response;
  }

  async fetchBookedTables(date: Date): Promise<TableId[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("bookedTables")
      .addQuery("date", toISODateString(date))
      .build();

    return await (await this.get(url, { authorize: true })).json();
  }

  async fetchLockedTables(date: Date): Promise<LockedTable[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("lockedTables")
      .addQuery("date", toISODateString(date))
      .build();

    const result = await (await this.get(url, { authorize: true })).json();

    return result;
  }

  async fetchBookedTablesByPeriod(period: DatePeriod): Promise<TableId[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("bookedTables")
      .addQuery("from", period.from.toISOString())
      .addQuery("to", period.to.toISOString())
      .build();
    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return json.map(TableIdSchema.parse);
  }

  async fetchLockedTablesByPeriod(period: DatePeriod): Promise<LockedTable[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("lockedTables")
      .addQuery("from", period.from.toISOString())
      .addQuery("to", period.to.toISOString())
      .build();
    const response = await this.get(url, { authorize: true });
    const json = await response.json();
    return json.map(LockedTableSchema.parse);
  }

  async lockTables(args: LockTableArgs): Promise<void> {
    const url = new UrlBuilder(this.getUrl()).addPath("lockedTables").build();
    await this.post(url, JSON.stringify(args), { authorize: true });
  }

  async unlockTables(args: UnlockTableArgs): Promise<void> {
    const url = new UrlBuilder(this.getUrl()).addPath("lockedTables").build();
    await this.delete(url, JSON.stringify(args), { authorize: true });
  }

  async fetchBookings({
    period,
    page,
    pageSize,
  }: FetchBookingsRequest): Promise<Booking[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addQuery("from", period.from.toISOString())
      .addQuery("to", period.to.toISOString())
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .build();

    const response = await (await this.get(url, { authorize: true })).json();

    const deserialized: Array<Booking> = response.map(BookingSchema.parse);

    return deserialized;
  }

  async fetchBooking(id: string): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addPath(id)
      .build();

    const response = await (await this.get(url, { authorize: true })).json();
    const deserialized = BookingSchema.parse(response);

    return deserialized;
  }

  async acceptBooking(id: string): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addPath(id)
      .addPath("accept")
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();
    const deserialized = BookingSchema.parse(response);

    return deserialized;
  }

  async rejectBooking({
    bookingId,
    message,
  }: RejectBookingRequest): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addPath(bookingId)
      .addPath("reject")
      .build();

    const response = await this.post(
      url,
      JSON.stringify({ rejectionMessage: message }),
      { authorize: true },
    );
    const deserialized = BookingSchema.parse(await response.json());

    return deserialized;
  }

  async beginBooking(id: string): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addPath(id)
      .addPath("begin")
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();
    const deserialized = BookingSchema.parse(response);

    return deserialized;
  }

  async endBooking(id: string): Promise<Booking> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("bookings")
      .addPath(id)
      .addPath("end")
      .build();

    const response = await (
      await this.post(url, EmptyBody, { authorize: true })
    ).json();
    const deserialized = BookingSchema.parse(response);

    return deserialized;
  }

  async fetchStripeIsPaymentAccountComplete(): Promise<boolean> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("stripe")
      .addPath("paymentAccountStatus")
      .build();

    const response = await this.get(url, { authorize: true });

    return (await response.json()).status === "complete" ? true : false;
  }

  async fetchStripeOnboardingLink(
    request: FetchStripeOnboardingLinkRequest,
  ): Promise<string> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("stripe")
      .addPath("onboardingLink")
      .addQuery("redirectUrl", request.redirectUrl)
      .addQuery("refreshUrl", request.refreshUrl)
      .build();

    const response = await this.get(url, { authorize: true });

    return (await response.json()).onboardingLink as string;
  }

  async fetchStripeLoginLink(): Promise<FetchStripeLoginLinkResult> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("stripe")
      .addPath("loginLink")
      .build();

    const response = await this.get(url, { authorize: true });

    return (await response.json()) as FetchStripeLoginLinkResult;
  }

  async createProduct(
    request: CreateProductRequest,
    requestOpts?: RequestOptions,
  ): Promise<VenueProduct> {
    requestOpts ??= { apiVersion: this.apiVersion };
    const url = new UrlBuilder(this.getUrl()).addPath("products").build();
    const result = await this.post(url, JSON.stringify(request), {
      authorize: true,
      headersVisitor: withApiVersion(requestOpts.apiVersion!),
    });
    return VenueProductSchema.parse(await result.json());
  }

  async updateProduct(
    request: UpdateProductRequest,
    requestOpts?: RequestOptions,
  ): Promise<VenueProduct> {
    requestOpts ??= { apiVersion: this.apiVersion };

    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addPath(request.id)
      .build();

    const result = await this.patch(url, JSON.stringify(request), {
      authorize: true,
      headersVisitor: withApiVersion(requestOpts.apiVersion!),
    });
    return VenueProductSchema.parse(await result.json());
  }

  async deleteProduct(id: string, requestOpts?: RequestOptions): Promise<void> {
    requestOpts ??= { apiVersion: this.apiVersion };

    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addPath(id)
      .build();

    await this.delete(url, EmptyBody, {
      authorize: true,
      headersVisitor: withApiVersion(requestOpts.apiVersion!),
    });
  }
  async fetchProduct(
    id: string,
    requestOpts?: RequestOptions,
  ): Promise<VenueProduct> {
    requestOpts ??= { apiVersion: this.apiVersion };

    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addPath(id)
      .build();

    const result = await this.get(url, {
      authorize: true,
      headersVisitor: withApiVersion(requestOpts.apiVersion!),
    });
    const json = await result.json();
    const parsed = VenueProductSchema.parse(json);
    return parsed;
  }

  async uploadAsset({ data }: UploadAssetRequest): Promise<ImageAsset> {
    const url = new UrlBuilder(this.getUrl()).addPath("venueAssets").build();

    const form = new FormData();
    form.set("file", data);

    const headersWithAuth = await this.createHeadersWithAuth();
    headersWithAuth.delete("Content-Type");
    headersWithAuth.delete("Content-Length");
    const response = await fetch(url, {
      method: "POST",
      headers: headersWithAuth,
      body: form,
    });
    return (await response.json()) as ImageAsset;
  }

  async fetchProducts(
    { page, pageSize }: PageRequest,
    requestOpts?: { apiVersion?: ApiVersions },
  ): Promise<Array<VenueProduct>> {
    requestOpts ??= { apiVersion: this.apiVersion };

    const url = new UrlBuilder(this.getUrl())
      .addPath("products")
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .build();
    const json = await this.get(url, {
      authorize: true,
      headersVisitor: withApiVersion(requestOpts.apiVersion!),
    });
    return Product.sortByPriceAndPriority(VenueProductsSchema.parse(json));
  }

  protected getUrl() {
    return this.url;
  }

  async acceptOrder(orderId: string): Promise<boolean> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addPath(orderId)
      .addPath("accept")
      .build();

    const result = await this.patch(url, EmptyBody, { authorize: true });
    const json = await result.json();
    return json.ok;
  }

  async rejectOrder(orderId: string, message: string): Promise<boolean> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addPath(orderId)
      .addPath("reject")
      .build();

    const result = await this.patch(url, JSON.stringify({ message: message }), {
      authorize: true,
    });
    const json = await result.json();
    return json.ok;
  }

  async subscribeToOrderNotifications(registrationId: string) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addPath("notifications")
      .addPath("subscribe")
      .addPath(registrationId)
      .build();
    await this.post(url, EmptyBody, { authorize: true });
  }

  async subscribeToNotifications(registrationId: string): Promise<void> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("pushNotificationSubscriptions")
      .addPath(registrationId)
      .build();
    await this.post(url, EmptyBody, { authorize: true });
  }

  async fetchPricedCapacities(): Promise<PricedCapacity[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("pricedCapacities")
      .build();

    const result = await this.get(url, { authorize: true });
    return (await result.json()) as PricedCapacity[];
  }

  async updateVenue(request: UpdateVenueRequest): Promise<Venue> {
    const url = new UrlBuilder(this.getUrl()).addPath("venue").build();
    const response = await this.patch(url, jsonify(request), {
      authorize: true,
    });
    const json = await response.json();
    const deserialized = VenueSchema.parse(json);
    return deserialized;
  }

  async updateSpecialOpeningHours(
    args: UpdateSpecialOpeningHoursArgs,
  ): Promise<SpecialOpeningHours[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("specialOpeningHours")
      .build();

    const result = await this.post(
      url,
      JSON.stringify(args.specialOpeningHours),
      { authorize: true },
    );
    const parsed = ManySpecialOpeningHoursDeserializer.parse(
      await result.json(),
    );

    return parsed.map((p) => ({
      id: p.id,
      period: new DatePeriod({ from: p.period.from, to: p.period.to }),
    }));
  }

  async fetchSpecialOpeningHours(): Promise<SpecialOpeningHours[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("specialOpeningHours")
      .build();

    const result = await this.get(url, { authorize: true });
    const parsed = ManySpecialOpeningHoursDeserializer.parse(
      await result.json(),
    );

    return parsed.map((p) => ({
      id: p.id,
      period: new DatePeriod({ from: p.period.from, to: p.period.to }),
    }));
  }

  async fetchVenue() {
    const url = new UrlBuilder(this.getUrl()).addPath("venue").build();
    const result = await this.get(url, { authorize: true });
    return VenueSchema.parse(await result.json());
  }

  async fetchVenues(): Promise<Venue[]> {
    const url = new UrlBuilder(this.getUrl()).addPath("venues").build();
    const result = await this.get(url, { authorize: true });
    if (Array.isArray(result)) {
      throw new Error(
        `the result from ${this.fetchVenues.name}() is not an array`,
      );
    }
    return (await result.json()).map((j: any) => VenueSchema.parse(j));
  }

  async selectCurrentVenue(venueId: string): Promise<void> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("venue")
      .addPath("current")
      .addPath(venueId)
      .build();
    await this.post(url, EmptyBody, { authorize: true });
  }

  async fetchOrders({
    page = 0,
    pageSize = 10,
  }: FetchOrdersRequest): Promise<Order[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("orders")
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .build();

    const result = await this.get(url, { authorize: true });
    const parsed = (await result.json()).map(OrderSchema.parse);
    return parsed;
  }

  async fetchMusicGenres(
    { page, pageSize }: PageRequest = { page: 1, pageSize: 100 },
  ) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("musicgenres")
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .build();

    return (await (
      await this.get(url, { authorize: true })
    ).json()) as Array<MusicGenre>;
  }

  async fetchThemes(
    { page, pageSize }: PageRequest = { page: 1, pageSize: 1000 },
  ) {
    const url = new UrlBuilder(this.getUrl())
      .addPath("themes")
      .addQuery("page", page)
      .addQuery("pageSize", pageSize)
      .build();

    return this.themeDeserializer.deserializeAll(
      await (await this.get(url, { authorize: true })).json(),
    );
  }

  async fetchGreeterEvents(
    request?: FetchGreeterEventsRequest,
  ): Promise<GreeterEvent[]> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addQuery("period", JSON.stringify(request?.period))
      .addQuery("direction", request?.direction)
      .addQuery("page", request?.page)
      .addQuery("pageSize", request?.pageSize)
      .build();

    return this.greeterEventDeserializer.deserializeAll(
      await (await this.get(url, { authorize: true })).json(),
    );
  }

  async createGreeterEvent(
    request: CreateGreeterEventRequest,
  ): Promise<GreeterEvent> {
    const url = new UrlBuilder(this.getUrl()).addPath("greeterEvents").build();

    const response = await this.post(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();
    return GreeterEventSchema.parse(json);
  }

  async updateGreeterEvent(
    request: UpdateGreeterEventRequest,
  ): Promise<GreeterEvent> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addPath(request.id)
      .build();

    const response = await this.patch(url, JSON.stringify(request), {
      authorize: true,
    });
    const json = await response.json();
    return GreeterEventSchema.parse(json);
  }

  async deleteGreeterEvent(id: string): Promise<void> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addPath(id)
      .build();

    await this.delete(url, EmptyBody, { authorize: true });
  }

  async fetchGreeterEvent(id: string): Promise<GreeterEvent> {
    const url = new UrlBuilder(this.getUrl())
      .addPath("greeterEvents")
      .addPath(id)
      .build();

    return this.greeterEventDeserializer.deserialize(
      await (await this.get(url, { authorize: true })).json(),
    );
  }
}
